import { getBuildId } from './build-id';

export interface AssetCacheRules {
  /** Entries ending with `/` match a whole folder, any other entry matches exactly one file. */
  include: readonly string[];
  /** Same syntax as `include`; an `exclude` match always wins. */
  exclude: readonly string[];
}

/**
 * Which same-origin URLs get the `?v=<build id>` parameter, so that a deploy produces new URLs
 * and browsers stop serving old copies from their cache. Paths are relative to the app base.
 * Rules are compiled in on purpose: no drift between environments, nothing to validate at runtime.
 */
export const ASSET_CACHE_RULES: AssetCacheRules = {
  include: [
    'assets/i18n/',
    'assets/customizations/',
    'assets/config.json', // fetched by loadAppConfig()
    'assets/images/', // static images, through the assetUrl pipe
  ],
  exclude: [],
};

const VERSION_PARAM = 'v';

let protectedUrls: string[] = [];

const toAbsolute = (url: string): URL | undefined => {
  try {
    return new URL(url, document.baseURI);
  } catch {
    return undefined;
  }
};

/**
 * URLs taken from the content of `config.json` (`apiBaseUrl`, `faro.url`) are never stamped,
 * whatever the rules say, so API and logging traffic stay safe if `include` is ever widened.
 * Cross-origin values are already ignored because only same-origin URLs are stamped.
 */
export const setProtectedUrls = (urls: ReadonlyArray<string | null | undefined>): void => {
  protectedUrls = urls
    .map((url) => (url ? toAbsolute(url) : undefined))
    .filter((url): url is URL => !!url)
    .map((url) => (url.href.endsWith('/') ? url.href.slice(0, -1) : url.href));
};

const isProtected = (target: URL): boolean => {
  const href = target.href;
  return protectedUrls.some((base) => href === base || ['/', '?', '#'].some((sep) => href.startsWith(base + sep)));
};

const matches = (entries: readonly string[], relativePath: string): boolean =>
  entries.some((entry) => (entry.endsWith('/') ? relativePath.startsWith(entry) : relativePath === entry));

const relativeToBase = (target: URL): string => {
  const basePath = new URL(document.baseURI).pathname;
  const path = target.pathname;
  return path.startsWith(basePath) ? path.slice(basePath.length) : path.replace(/^\//, '');
};

/** True when the URL is same-origin, not protected, and matches the rules. */
export const isVersioned = (url: string, rules: AssetCacheRules = ASSET_CACHE_RULES): boolean => {
  const target = toAbsolute(url);
  if (!target || target.origin !== window.location.origin || isProtected(target)) {
    return false;
  }
  const relativePath = relativeToBase(target);
  return !matches(rules.exclude, relativePath) && matches(rules.include, relativePath);
};

/**
 * Appends `v=<build id>` to a URL that matches the rules, keeping the other query parameters
 * and the fragment; an existing `v` is replaced. Any other URL is returned unchanged.
 * Keep the output shape in sync with `appendVersionParam` in `packages/tofe-cli/src/commands/assets/stamp.ts`.
 */
export const withVersion = (url: string, buildId: string = getBuildId()): string => {
  if (!isVersioned(url)) {
    return url;
  }
  const hashIndex = url.indexOf('#');
  const fragment = hashIndex >= 0 ? url.slice(hashIndex) : '';
  const withoutFragment = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const queryIndex = withoutFragment.indexOf('?');
  const path = queryIndex >= 0 ? withoutFragment.slice(0, queryIndex) : withoutFragment;
  const query = queryIndex >= 0 ? withoutFragment.slice(queryIndex + 1) : '';
  const params = query
    .split('&')
    .filter((param) => param && param !== VERSION_PARAM && !param.startsWith(`${VERSION_PARAM}=`));
  params.push(`${VERSION_PARAM}=${encodeURIComponent(buildId)}`);
  return `${path}?${params.join('&')}${fragment}`;
};
